//#region Function Imports
import React, { useState, useEffect } from 'react';
import {
    StyleSheet, SafeAreaView, Pressable,
    FlatList, View, Platform, useWindowDimensions, Image, ActivityIndicator
} from 'react-native';
import { Card , Provider} from 'react-native-paper';
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useLeave } from '../../../hooks/useLeave';
import { useTranslation } from 'react-i18next';
import { DEFAULTPROFILE } from '../../../img/DefaultProfile'
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from "react-native-safe-area-context";
//#endregion

const TeamsLeaveSchedulerScreen = (props) => {

    //#region Function State Definitions
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const styles = makeStyles(colors, props, height, width);
    const navigation = useNavigation();
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [selectedData, setSelectedData] = useState([]);
    const [deptFilterList, setDeptFilterList] = useState([])
    const [selectedFilterDept, setSelectedFilterDept] = useState("");
    const { isLoading: useLeaveIsLoading, GetUpcomingLeavesCalendar } = useLeave();
    const [range, setRange] = useState({ startDate: null, endDate: null });
    const [tempRange, setTempRange] = useState({});
    const [rangeText, setRangeText] = useState("");
    const [selectedRange, setSelectedRange] = useState({ startDate: null, endDate: null });
    const insets = useSafeAreaInsets();

    useEffect(() => {
        if (selectedRange.startDate && selectedRange.endDate) {
            const formattedStart = changeDateFromYYYYMMDDToDDMMYYYY(selectedRange.startDate);
            const formattedEnd = changeDateFromYYYYMMDDToDDMMYYYY(selectedRange.endDate);
            setRangeText(formattedStart === formattedEnd ? formattedStart : `${formattedStart} - ${formattedEnd}`);
        } else {
            setRangeText("");
        }
    }, [selectedRange]);

    const changeDateFromYYYYMMDDToDDMMYYYY = (dateString) => {
        if (!dateString) return '';
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        if (!year || !month || !day) return '';
        return `${day}/${month}/${year}`;
    };


    useEffect(() => {
        if (
            (tempRange.startDate === '' || tempRange.startDate === null || tempRange.startDate === undefined) &&
            (tempRange.endDate === '' || tempRange.endDate === null || tempRange.endDate === undefined)
        ) {
            setSelectedRange({ startDate: null, endDate: null });
            setRange({ startDate: null, endDate: null });
            setRangeText('');
            if (props.LAA) {
                fetchLeaves(null, null);
            }
        }
    }, [tempRange]);

    const formatToISO = (d) => {
        if (!d) return null;
        // if already a Date
        const dateObj = (d instanceof Date) ? d : new Date(d);
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const aggregateSelectedData = (rows, selectedRange) => {
        const map = new Map();
        const start = selectedRange?.startDate ? new Date(selectedRange.startDate) : null;
        const end = selectedRange?.endDate ? new Date(selectedRange.endDate) : null;

        (rows || []).forEach(r => {
            const empId = r.teammateName;
            const leaveDateStr = r.linedate || r.startdate;
            if (!leaveDateStr) return;
            const leaveDate = new Date(leaveDateStr.split('T')[0]);
            if (start && end && (leaveDate < start || leaveDate > end))
                return;
            if (!map.has(empId)) {
                map.set(empId, {
                    employeeId: empId,
                    teammateName: r.teammateName,
                    acodeDesc: r.acodeDesc,
                    employeePhoto: r.employeePhoto || '',
                    approved: !!r.approved,
                    submitted: !!r.submitted,
                    department: r.department,
                    leaveDates: [leaveDateStr]
                });
            } else {
                const cur = map.get(empId);
                cur.approved = cur.approved || !!r.approved;
                cur.submitted = cur.submitted || !!r.submitted;
                if (!cur.leaveDates.includes(leaveDateStr)) {
                    cur.leaveDates.push(leaveDateStr);
                }
            }
        });

        return Array.from(map.values());
    };

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const allLeaves = await GetUpcomingLeavesCalendar(null, null);
                setOriginalData(allLeaves || []);
                setData(allLeaves || []);
                setDeptFilterList([...new Set((allLeaves || []).map(r => r.department))].map(d => ({ label: d, value: d })));
                setSelectedData([]);
            } catch (err) {
                console.error('Failed to load initial leaves data', err);
            }
        };
        loadInitialData();
    }, []);

    const fetchLeaves = async (startISO, endISO) => {
        const activeRange = (startISO && endISO)
            ? { startDate: startISO, endDate: endISO }
            : { startDate: null, endDate: null };

        try {
            if (GetUpcomingLeavesCalendar.length >= 2) {
                const resp = await GetUpcomingLeavesCalendar(startISO, endISO);
                setData(resp || []);
                setOriginalData(resp || []);
                setDeptFilterList([...new Set((resp || []).map(r => r.department))].map(d => ({ label: d, value: d })));
                setSelectedData(aggregateSelectedData(resp || [], activeRange));
                return;
            }
        } catch (err) {
            console.error('Server fetch failed, falling back to client filter', err);
        }

        // Fallback client-side filtering
        const parsedStart = startISO ? new Date(startISO) : null;
        const parsedEnd = endISO ? new Date(endISO) : null;

        const filtered = (originalData || []).filter(item => {
            let itemStart = item.linedate ? new Date(item.linedate)
                : item.startdate ? new Date(item.startdate)
                    : item.date ? new Date(item.date)
                        : null;

            let itemEnd = item.enddate ? new Date(item.enddate) : itemStart;

            if (!itemStart || isNaN(itemStart.getTime())) return false;
            if (!itemEnd || isNaN(itemEnd.getTime())) itemEnd = itemStart;
            if (!parsedStart || !parsedEnd) return true;

            return !(itemEnd < parsedStart || itemStart > parsedEnd);
        });

        setData(filtered);
        setSelectedData(aggregateSelectedData(filtered, activeRange));
        setDeptFilterList([...new Set(filtered.map(r => r.department))].map(d => ({ label: d, value: d })));
    };

    const onRangeConfirm = (e) => {
        if (Platform.OS === 'web' && e && e.preventDefault) e.preventDefault();
        const s = tempRange.startDate;
        const en = tempRange.endDate || tempRange.startDate;
        if (!s) return;
        const startISO = formatToISO(s);
        const endISO = formatToISO(en);
        setRange({ startDate: startISO, endDate: endISO });
        setSelectedRange({ startDate: startISO, endDate: endISO });
        setSelectedFilterDept('');
        fetchLeaves(startISO, endISO);
    };

    useEffect(() => {
        return () => {
            onClearRange();
        };
    }, []);

    const onClearRange = () => {
        setTempRange({});
        setSelectedRange({ startDate: null, endDate: null });
        setRange({ startDate: null, endDate: null });
        setRangeText("");
        setSelectedFilterDept('');
        setData(originalData);
        setSelectedData(aggregateSelectedData(data, { startDate: null, endDate: null }));
    };

    //#region Render Functions
    const renderSelectedPeople = ({ item }) => {
        let status = '';
        let specificColor = '';
        let textColor = '';
        if (item.approved) {
            status = 'APPROVED'
            specificColor = colors.background_approved
            textColor = colors.text_approved
        }
        if (item.submitted && !item.approved) {
            status = 'SUBMITTED'
            specificColor = colors.background_submitted
            textColor = colors.text_pending
        }
        if (!item.submitted && !item.approved) {
            status = 'NOT SUBMITTED'
            specificColor = colors.background_not_submitted
            textColor = colors.text_not_submitted
        }
        
        return (
            Platform.OS === 'web' && width > 500 ?
                <View style={styles.rowDirection}>
                    <Image
                        style={{
                            width: 30,
                            height: 30,
                            marginRight: 10,
                            borderRadius: 100,
                            borderWidth: 1,
                            justifyContent: 'center',
                        }}
                        source={{ uri: `data:image/png;base64,${item.employeePhoto === "" ? DEFAULTPROFILE : item.employeePhoto}` }}
                    />
                    <View style={styles.subView}>
                        <CustomLabel
                            value={item.teammateName}
                            type='AVAILABLEUSED_SECONDARY' />
                        <CustomLabel
                            value={item.acodeDesc}
                            type='AVAILABLEUSED_MUTED' />
                    </View>
                    {!props.LAA && (
                        <CustomLabel
                            value={`${item.leaveDates
                                .map(dateStr => changeDateFromYYYYMMDDToDDMMYYYY(dateStr))
                                .join(', ')}`}
                            type='AVAILABLEUSED_MUTED'
                            style={{ marginTop: 4 }}
                        />
                    )}
                    <CustomLabel
                        value={status}
                        type='STATUS'
                        specificColor={specificColor}
                        textColor={textColor} />
                </View> :
                !props.LAA ? <RenderLeaveCards item={item} status={status} specificColor={specificColor} textColor={textColor}/> : 
                <View style={{ width: width, marginTop: 15, flexDirection: "row",  marginLeft: 4,}}>
                    <Image
                        style={{
                            width: 30,
                            height: 30,
                            marginRight: 1,
                            borderRadius: 100,
                            borderWidth: 1,
                            justifyContent: 'center',
                        }}
                        source={{ uri: `data:image/png;base64,${item.employeePhoto === "" ? DEFAULTPROFILE : item.employeePhoto}` }}
                    />
                    <View
                        style={styles.spaceBetween}>
                        <CustomLabel
                            value={item.teammateName}
                            type='AVAILABLEUSED_SECONDARY' />
                        <CustomLabel
                            value={item.acodeDesc}
                            type='AVAILABLEUSED_SECONDARY' />
                        <CustomLabel
                            value={status}
                            type='STATUS'
                            specificColor={specificColor}
                            textColor={textColor} />
                    </View>
                </View>
                
        )
    };

    function RenderLeaveCards( params ) {
        const [showDetails, setShowDetails] = useState(false);
        const onShowHidePress = (item) => {
            setShowDetails(!showDetails);
        };

        return (
            <Provider>
                <Card
                    style={styles.cardstyleApp}>
                    <Card.Content>
                        <Pressable onPress={() => onShowHidePress(params.item)}>
                            <View style={{ width: width, marginTop: 15, flexDirection: "row",  marginLeft: 4}}>
                                <Image
                                    style={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: 100,
                                        borderWidth: 1,
                                        justifyContent: 'center',
                                    }}
                                    source={{ uri: `data:image/png;base64,${params.item.employeePhoto === "" ? DEFAULTPROFILE : params.item.employeePhoto}` }}
                                />
                                <View
                                    style={{flex: 1, flexDirection: 'column', alignItems: 'flex-start', paddingLeft: 40 }}>
                                    <CustomLabel
                                        value={params.item.teammateName}
                                        type='AVAILABLEUSED_SECONDARY' />
                                    <CustomLabel
                                        value={params.item.acodeDesc}
                                        type='AVAILABLEUSED_SECONDARY' />
                                    <CustomLabel
                                        value={params.status}
                                        type='STATUS'
                                        specificColor={params.specificColor}
                                        textColor={params.textColor} />
                                </View>
                            </View>
                        </Pressable>
                        {showDetails === true && 
                        <SafeAreaView style={{marginVertical: 10}}>
                            <CustomLabel
                                value={`${params.item.leaveDates
                                    .map(dateStr => changeDateFromYYYYMMDDToDDMMYYYY(dateStr))
                                    .join(', ')}`}
                                type='AVAILABLEUSED_MUTED'
                                style={{ marginTop: 4 }}
                            />
                        </SafeAreaView>}
                    </Card.Content>
                </Card >
            </Provider>
        )
        // }
    };
    //#endregion

    //#region UseEffect
    // navigation
    useEffect(() => {
        navigation.setOptions({
            title: t('navigate:TSC')
        });
    }, []);

    useEffect(() => {
        setSelectedData(selectedData)
    }, [selectedData])

    useEffect(() => {
        if (props.LAA) {
            const todayISO = new Date().toISOString().split('T')[0];
            const filteredByToday = data.filter(obj => {
                const d = (obj.linedate || obj.startdate || '').split('T')[0];
                return d === todayISO;
            });
            setSelectedData(aggregateSelectedData(filteredByToday, { startDate: todayISO, endDate: todayISO }));
        } else {
            // Only filter if a date range is selected
            if (selectedRange.startDate && selectedRange.endDate) {
                let filteredData = data;
                if (selectedFilterDept) {
                    filteredData = filteredData.filter(obj => obj.department === selectedFilterDept);
                }
                setSelectedData(aggregateSelectedData(filteredData, selectedRange));
            } else {
                setSelectedData([]);
            }
        }
    }, [selectedFilterDept, data, selectedRange, props.LAA]);

    //#endregion

    if (useLeaveIsLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )

    return (
        <ScrollView style={[styles.mainView, {paddingBottom: Math.max(insets.bottom + 24, 40)}]}>
            {props.LAA !== true && (
                <CustomLabel
                    value={t('navigate:TSC')}
                    type='MINORTITLE'
                />
            )}
            <SafeAreaView style={styles.row}>
                <CustomLabel
                    value={t('common:deptCalendarFilter')}
                    type='NEWREQUESTLABEL'
                />
                <CustomDropDown
                    options={deptFilterList}
                    value={selectedFilterDept}
                    type="CALENDARFILTER"
                    setValue={(e) => setSelectedFilterDept(e)}
                    label={"Select"}
                />
            </SafeAreaView>

            {!props.LAA && (
                <>
                    <View style={styles.leaveDatesContainer}>
                        <CustomLabel
                            value={`${t('common:LD')}`}
                            type='NEWREQUESTLABEL'
                            style={styles.leaveDatesLabel}
                        />
                        <CustomInput
                            type='FULL'
                            placeholder={t('common:selectDate')}
                            value={rangeText}
                            editable={false}
                            style={styles.leaveDatesInput}
                        />
                    </View>

                    <View style={styles.osDirection}>
                        <View style={styles.calendarAndButtonContainer}>
                            <Card style={styles.cardstyle}>
                                <CustomCalendar
                                    calendarType={'RangePicker'}
                                    data={data}
                                    setRange={setTempRange}
                                    range={tempRange}
                                    selectedData={selectedData}
                                    setSelectedData={setSelectedData}
                                    selectedRange={selectedRange}
                                    setSelectedRange={setSelectedRange}
                                    onClear={onClearRange}
                                />
                            </Card>
                            <View style={styles.selectDateRangeButtonContainer}>
                                <CustomButton
                                    text={t('common:selectDateRange')}
                                    onPress={(e) => onRangeConfirm(e)}
                                    type='SAVE_CALENDAR_DATES_WEB'
                                />
                            </View>
                        </View>

                        {selectedData.length !== 0 ? (
                            <View style={[styles.cardstyleTransparent, {paddingBottom: Math.max(insets.bottom + 24, 40)}]}>
                                <FlatList
                                    data={selectedData}
                                    renderItem={renderSelectedPeople}
                                    scrollEnabled={false}
                                    nestedScrollEnabled={false}
                                    keyExtractor={(item, index) => `${item.employeeId ?? item.teammateName}-${index}`}
                                />
                            </View>
                        ) : (
                            <View style={[styles.cardstyleTransparent, {width: Platform.OS === 'web' ? '100%' : width, paddingBottom: Math.max(insets.bottom + 24, 40)}]}>
                                <CustomLabel
                                    value={t('common:noLeavesOnSelectedDate')}
                                    type='MAIN_LABEL_TEAM_SCHEDULE'
                                />
                            </View>
                        )}
                    </View>
                </>
            )}

            {props.LAA && (
                <View style={styles.osDirectionLAA}>
                    <Card style={styles.cardstyle}>
                        <CustomCalendar
                            data={data}
                            calendarType='Multidot'
                            setSelectedData={setSelectedData}
                            selectedData={selectedData}
                            selectedDate={props.date}
                            setCurrDate={null}
                        />
                    </Card>

                    {selectedData.length !== 0 ? (
                        <View style={styles.cardstyleTransparent}>
                            <FlatList
                                data={selectedData}
                                renderItem={renderSelectedPeople}
                            />
                        </View>
                    ) : (
                        <View style={styles.cardstyleTransparent}>
                            <CustomLabel
                                value={t('common:noLeavesOnSelectedDate')}
                                type='MAIN_LABEL_TEAM_SCHEDULE'
                            />
                        </View>
                    )}
                </View>
            )}

            {props.date && props.LAA === false && (
                <CustomButton
                    text={t('navigate:LAA')}
                    onPress={() => {
                        props.selectLeaveApplicationApprovalScreen();
                    }}
                    iconName='caret-back-outline'
                    sz={20}
                    type='BACK_TO_LAA'
                    show='BACK_TO_LAA'
                    textTop='0'
                />
            )}
        </ScrollView>
    );
}


//#region Function Styles
const makeStyles = (colors, props, height, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginLeft: '2%',
        marginRight: '2%',
        marginTop: Platform.OS === 'web' ? '1%' : 0,
    },
    loader: {
        padding: 80
    },
    subView: {
        flex: 1,
        marginHorizontal: 20,
        alignItems: (Platform.OS === 'web' && width > 1200) ? 'space-evenly' : 'flex-start'
    },
    rowDirection: {
        flex: 1,
        flexDirection: 'row',
        marginHorizontal: 20,
        marginVertical: '1%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    osDirection: {
        margin: '1%',
        flexDirection: (Platform.OS === 'web' && width > 1200) ? "row" : "column",
        justifyContent: (Platform.OS === 'web' && width > 1200) ? "space-evenly" : "flex-start",
        alignItems: (Platform.OS === 'web' && width > 1200) ? "space-evenly" : "center",
        width: '100%',
    },
    osDirectionLAA: {
        marginHorizontal: 10,
        flexDirection: "column",
        justifyContent: (Platform.OS === 'web' && width > 1300) ? "space-evenly" : "flex-start",
        alignItems: (Platform.OS === 'web' && width > 1300) ? "space-evenly" : "center",
        width: '100%',
    },
    cardstyle: {
        height: Platform.OS === 'web' ? 285 : (height < 670 ? height * 45 / 100 : height * 37 / 100),
        width: Platform.OS === 'web' ? 385 : width * 90 / 100,
        borderRadius: 10,
        elevation: 10,
        marginRight: 5
    },
    cardstyleApp: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        borderColor: colors.buttons_submit,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background,
    },
    cardstyleTransparent: {
        flex: 1,
        margin: 15,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        elevation: 0,
    },
    row: {
        flex: 1,
        marginTop: 15,
        flexDirection:  Platform.OS === 'web' ? "row" : 'column',
        marginLeft: 4,
    },
    spaceBetween: {
        flex: 1,
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        alignItems: 'center',
        marginLeft: 5,
    },
    spaceStatus: {
        marginTop: '5%',
        marginLeft: '5%',
        marginRight: '5%'
    },
    leaveDatesContainer: {
        flexDirection:  Platform.OS === 'web' ? 'row' : 'column',
        alignItems: Platform.OS === 'web' ? 'center' : 'flex-start',
        marginVertical: 10,
        width: '100%',
        maxWidth: 400,
    },
    leaveDatesLabel: {
        flex: 1,
        marginLeft: 4,
        fontWeight: '600',
        fontSize: 14,
        color: colors.text_primary,
    },
    leaveDatesInput: {
        flex: 2,
        height: 35,
        marginLeft: 10,
        paddingVertical: 5,
        fontSize: 14,
    },
    selectDateRangeButtonContainer: {
        marginTop: 10,
        marginBottom: 20,
        width: 160,
        alignSelf: 'center'
    },
    calendarAndButtonContainer: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
});
//#endregion

export default TeamsLeaveSchedulerScreen;